import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    TableSortLabel,
    TextField,
    MenuItem,
    Grid,
    Skeleton,
    useTheme,
    useMediaQuery,
    Fab
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { transactionsAPI, categoriesAPI, paymentModesAPI, membersAPI } from '../api/api';
import { formatCurrency, formatDate, getCategoryStyle, LABELS } from '../config';
import AdvancedFilters from './common/AdvancedFilters';
import { useAuth } from '../contexts/AuthContext';

const Transactions = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { user } = useAuth();

    const [transactions, setTransactions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [members, setMembers] = useState([]);
    const [paymentModes, setPaymentModes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        categoryId: '',
        memberId: '',
        search: '',
        type: '',
        sortBy: 'transaction_date',
        sortOrder: 'DESC'
    });

    useEffect(() => {
        fetchData();
    }, [filters]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [txnRes, catRes, pmRes, memRes] = await Promise.all([
                transactionsAPI.getAll(filters),
                categoriesAPI.getActive(),
                paymentModesAPI.getAll(),
                membersAPI.getActive()
            ]);

            if (txnRes.data.success) {
                setTransactions(txnRes.data.data);
            }
            if (catRes.data.success) {
                setCategories(catRes.data.data);
            }
            if (pmRes.data.success) {
                setPaymentModes(pmRes.data.data);
            }
            if (memRes.data.success) {
                setMembers(memRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
            toast.error('Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = () => {
        fetchData();
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Transaction?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete'
        });

        if (result.isConfirmed) {
            try {
                await transactionsAPI.delete(id);
                toast.success('Transaction deleted');
                fetchData();
            } catch (error) {
                toast.error('Failed to delete transaction');
            }
        }
    };


    // Mobile Card View
    const MobileCard = ({ txn }) => (
        <Card sx={{ mb: 2 }} className="hover-lift">
            <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1, mr: 1 }}>
                        {txn.particulars}
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color={txn.category_type === 'EXPENSE' ? 'error' : 'success.main'}
                    >
                        {txn.category_type === 'EXPENSE' ? '-' : '+'}{formatCurrency(txn.amount)}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {formatDate(txn.transaction_date)}
                        </Typography>
                        <Chip
                            label={paymentModes.find(pm => pm.code === txn.payment_mode)?.name || txn.payment_mode || 'CASH'}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: 9 }}
                        />
                        <Chip
                            label={txn.category_name}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: 10,
                                bgcolor: getCategoryStyle(txn.category_name).bg,
                                color: getCategoryStyle(txn.category_name).text,
                                border: `1px solid ${getCategoryStyle(txn.category_name).border}`,
                                fontWeight: 600
                            }}
                        />
                    </Box>
                    {user && user.role !== 'VIEWER' && (
                        <Box>
                            <IconButton size="small" onClick={() => navigate(`/transactions/${txn.id}`)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(txn.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" fontWeight={700}>
                    Transactions
                </Typography>
                {!isMobile && user && user.role !== 'VIEWER' && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/transactions/new')}
                    >
                        Add Transaction
                    </Button>
                )}
            </Box>

            <AdvancedFilters
                filters={filters}
                onFilterChange={setFilters}
                configs={{
                    search: true,
                    dateRange: true,
                    type: true,
                    categories: true,
                    members: true,
                    sortBy: [
                        { label: 'Date', value: 'transaction_date' },
                        { label: 'Amount', value: 'amount' },
                        { label: 'Particulars', value: 'particulars' }
                    ]
                }}
                data={{ categories, members }}
            />

            {/* Content */}
            {loading ? (
                [...Array(5)].map((_, i) => (
                    <Skeleton key={i} height={80} sx={{ mb: 1 }} />
                ))
            ) : isMobile ? (
                // Mobile View
                <Box>
                    {transactions.map((txn) => (
                        <MobileCard key={txn.id} txn={txn} />
                    ))}
                    {transactions.length === 0 && (
                        <Card>
                            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">No transactions found</Typography>
                                {user && user.role !== 'VIEWER' && (
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        sx={{ mt: 2 }}
                                        onClick={() => navigate('/transactions/new')}
                                    >
                                        Add First Transaction
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Box>
            ) : (
                // Desktop Table View
                <Card className="next-gen-table">
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={filters.sortBy === 'transaction_date'}
                                            direction={filters.sortBy === 'transaction_date' ? filters.sortOrder.toLowerCase() : 'asc'}
                                            onClick={() => setFilters({
                                                ...filters,
                                                sortBy: 'transaction_date',
                                                sortOrder: filters.sortBy === 'transaction_date' && filters.sortOrder === 'DESC' ? 'ASC' : 'DESC'
                                            })}
                                        >
                                            Date
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={filters.sortBy === 'particulars'}
                                            direction={filters.sortBy === 'particulars' ? filters.sortOrder.toLowerCase() : 'asc'}
                                            onClick={() => setFilters({
                                                ...filters,
                                                sortBy: 'particulars',
                                                sortOrder: filters.sortBy === 'particulars' && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC'
                                            })}
                                        >
                                            Particulars
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={filters.sortBy === 'category_name'}
                                            direction={filters.sortBy === 'category_name' ? filters.sortOrder.toLowerCase() : 'asc'}
                                            onClick={() => setFilters({
                                                ...filters,
                                                sortBy: 'category_name',
                                                sortOrder: filters.sortBy === 'category_name' && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC'
                                            })}
                                        >
                                            Category
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={filters.sortBy === 'expense_type_name'}
                                            direction={filters.sortBy === 'expense_type_name' ? filters.sortOrder.toLowerCase() : 'asc'}
                                            onClick={() => setFilters({
                                                ...filters,
                                                sortBy: 'expense_type_name',
                                                sortOrder: filters.sortBy === 'expense_type_name' && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC'
                                            })}
                                        >
                                            Division Type
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        Member
                                    </TableCell>
                                    <TableCell>
                                        Payment Mode
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={filters.sortBy === 'amount'}
                                            direction={filters.sortBy === 'amount' ? filters.sortOrder.toLowerCase() : 'asc'}
                                            onClick={() => setFilters({
                                                ...filters,
                                                sortBy: 'amount',
                                                sortOrder: filters.sortBy === 'amount' && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC'
                                            })}
                                        >
                                            Amount
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.map((txn) => (
                                    <TableRow
                                        key={txn.id}
                                        hover
                                        onClick={() => navigate(`/transactions/${txn.id}`)}
                                        className="MuiTableRow-hover"
                                    >
                                        <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {txn.particulars}
                                                    </Typography>
                                                    {txn.remarks && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {txn.remarks}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {user && user.role !== 'VIEWER' && (
                                                    <Box className="row-actions">
                                                        <IconButton
                                                            className="action-icon-btn edit"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/transactions/${txn.id}`); }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            className="action-icon-btn delete"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleDelete(txn.id); }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={txn.category_name}
                                                size="small"
                                                sx={{
                                                    bgcolor: getCategoryStyle(txn.category_name).bg,
                                                    color: getCategoryStyle(txn.category_name).text,
                                                    border: `1px solid ${getCategoryStyle(txn.category_name).border}`,
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {txn.expense_type_name || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {txn.member_name || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={paymentModes.find(pm => pm.code === txn.payment_mode)?.name || txn.payment_mode || 'Cash'}
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                fontWeight={600}
                                                color={txn.category_type === 'EXPENSE' ? 'error' : 'success.main'}
                                            >
                                                {formatCurrency(txn.amount)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {transactions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No transactions found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            {/* Mobile FAB */}
            {isMobile && user && user.role !== 'VIEWER' && (
                <Fab
                    color="primary"
                    sx={{ position: 'fixed', bottom: 72, right: 16 }}
                    onClick={() => navigate('/transactions/new')}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};

export default Transactions;
